import { AdminPage } from "@/components/AdminPage";
import { AdminTable, type Column } from "@/components/AdminTable";
import { OverviewMetricChart } from "@/components/OverviewMetricChart";

function formatBRLCompact(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

type Row = {
  verification_id: string;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  amount_cents: number;
  discount_cents: number;
  applied_coupon_code: string | null;
  status: string;
  target_type: "pf" | "pj";
  target_document: string;
  created_at: string;
  completed_at: string | null;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

const STATUS_STYLE: Record<string, string> = {
  paid: "bg-accent-soft text-accent border-accent/30",
  processing: "bg-warning/10 text-warning border-warning/30",
  completed: "bg-accent-soft text-accent border-accent/30",
  failed: "bg-danger/10 text-danger border-danger/30",
  refunded: "bg-surface-alt text-text-tertiary border-border",
};

const COLUMNS: Column<Row>[] = [
  {
    header: "Cliente",
    width: "22%",
    cell: (r) => (
      <div>
        <p className="truncate text-text">{r.user_name ?? "—"}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.user_email}</p>
      </div>
    ),
  },
  {
    header: "Valor",
    width: "13%",
    cell: (r) => (
      <div>
        <p className="font-medium text-text">{formatBRL(r.amount_cents)}</p>
        {r.applied_coupon_code && (
          <p className="text-[10px] text-text-tertiary">
            {r.applied_coupon_code} (−{formatBRL(r.discount_cents)})
          </p>
        )}
      </div>
    ),
  },
  {
    header: "Status",
    width: "10%",
    cell: (r) => (
      <span
        className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
          STATUS_STYLE[r.status] ?? "bg-surface-alt text-text-tertiary border-border"
        }`}
      >
        {r.status}
      </span>
    ),
  },
  {
    header: "Stripe",
    width: "26%",
    hideBelow: "lg",
    cell: (r) => (
      <div className="text-[11px] text-text-tertiary">
        {r.stripe_session_id && (
          <p className="truncate">cs: <code>{r.stripe_session_id}</code></p>
        )}
        {r.stripe_payment_intent_id && (
          <p className="truncate">pi: <code>{r.stripe_payment_intent_id}</code></p>
        )}
        {!r.stripe_session_id && !r.stripe_payment_intent_id && "—"}
      </div>
    ),
  },
  {
    header: "Alvo",
    width: "11%",
    hideBelow: "md",
    cell: (r) => (
      <span className="text-[11px] text-text-secondary">
        {r.target_type.toUpperCase()}
      </span>
    ),
  },
  {
    header: "Datas",
    width: "18%",
    hideBelow: "sm",
    cell: (r) => (
      <div>
        <p className="text-[11px] text-text-secondary">{formatDateTime(r.created_at)}</p>
        {r.completed_at && (
          <p className="text-[10px] text-text-tertiary">
            ✓ {formatDateTime(r.completed_at)}
          </p>
        )}
      </div>
    ),
  },
];

export default function AdminPagamentosPage() {
  return (
    <AdminPage
      eyebrow="Pagamentos"
      title="Cobranças"
      description="Cada consulta paga gera uma cobrança via Stripe. Os ids abaixo (cs/pi) batem com o Stripe Dashboard."
    >
      <div className="mb-6">
        <OverviewMetricChart
          metric="grossRevenueCents"
          title="Receita bruta nos últimos 30 dias"
          format={formatBRLCompact}
        />
      </div>
      <AdminTable<Row>
        endpoint="/api/admin/payments"
        itemsKey="payments"
        rowKey={(r) => r.verification_id}
        columns={COLUMNS}
        searchPlaceholder="E-mail do cliente, session ou payment intent id"
        emptyTitle="Nenhuma cobrança ainda"
      />
    </AdminPage>
  );
}
